import * as React from "react";
import { useRef, useMemo, useState, useCallback } from "react";
import { cn } from "../../lib/utils";

export interface CodeEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minHeight?: string;
  readOnly?: boolean;
  className?: string;
}

const CodeEditor = React.forwardRef<HTMLTextAreaElement, CodeEditorProps>(
  (
    {
      value,
      onChange,
      placeholder = "",
      minHeight = "400px",
      readOnly = false,
      className,
    },
    ref
  ) => {
    const lineNumbersRef = useRef<HTMLDivElement>(null);
    const internalTextareaRef = useRef<HTMLTextAreaElement>(null);
    const textareaRef = (ref as React.RefObject<HTMLTextAreaElement>) || internalTextareaRef;
    
    const [activeLine, setActiveLine] = useState(1);

    // Calculate lines from value or placeholder
    const lines = useMemo(() => {
      const content = value || placeholder || "";
      const lineArray = content.split("\n");
      return lineArray.length < 20 
        ? [...lineArray, ...Array(20 - lineArray.length).fill("")]
        : lineArray;
    }, [value, placeholder]);

    // Sync scroll between line numbers and textarea
    const handleScroll = useCallback((e: React.UIEvent<HTMLTextAreaElement>) => {
      if (lineNumbersRef.current) {
        lineNumbersRef.current.scrollTop = e.currentTarget.scrollTop;
      }
    }, []);

    // Track active line based on cursor position
    const handleCursorChange = useCallback((e: React.SyntheticEvent<HTMLTextAreaElement>) => {
      const textarea = e.currentTarget;
      const lineNumber = textarea.value.substr(0, textarea.selectionStart).split("\n").length;
      setActiveLine(lineNumber);
    }, []);

    const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
      onChange(e.target.value);
    }, [onChange]);

    return (
      <div className={cn("code-editor-container border border-border rounded-lg overflow-hidden shadow-sm", className)}>
        {/* Editor Body - Single internal scroll */}
        <div className="code-editor-body flex" style={{ height: minHeight }}>
          {/* Line Numbers - synced scroll */}
          <div
            ref={lineNumbersRef}
            className="line-numbers bg-muted/40 text-muted-foreground text-right pr-3 pl-3 py-3 select-none font-mono text-sm leading-6 flex-shrink-0 border-r border-border overflow-hidden"
            style={{ minWidth: "48px" }}
          >
            {lines.map((_, i) => (
              <div
                key={i}
                className={cn(
                  "transition-colors",
                  activeLine === i + 1 && "text-primary font-medium"
                )}
              >
                {i + 1}
              </div>
            ))}
          </div>

          {/* Textarea */}
          <textarea
            ref={textareaRef}
            value={value}
            onChange={handleChange}
            onScroll={handleScroll}
            onClick={handleCursorChange}
            onKeyUp={handleCursorChange}
            placeholder={placeholder}
            readOnly={readOnly}
            className={cn(
              "flex-1 bg-background font-mono text-sm leading-6 p-3 resize-none focus:outline-none overflow-auto",
              "text-foreground placeholder:text-muted-foreground/50",
              readOnly && "cursor-default opacity-70"
            )}
            spellCheck={false}
          />
        </div>

        {/* Footer with line info */}
        <div className="code-editor-footer flex justify-between items-center px-4 py-1.5 bg-muted/30 border-t border-border text-xs text-muted-foreground">
          <span>Línea {activeLine}</span>
          <span>{value.split("\n").length} líneas · {value.length} caracteres</span>
        </div>
      </div>
    );
  }
);

CodeEditor.displayName = "CodeEditor";

export { CodeEditor };
